import { useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useDashboardAtivos } from '../hooks';
import { KpiCard } from '../components/dashboard/KpiCard';
import { RankingList } from '../components/dashboard/RankingList';
import { CategoryValueChart } from '../components/dashboard/CategoryValueChart';
import { CostCenterDonutChart } from '../components/dashboard/CostCenterDonutChart';
import { GlobalFilters } from '../components/dashboard/GlobalFilters';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { licencaUrl } from '../utils/links';

const brl = (v: string | number) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * SPEC 06 §2 — Dashboard de Ativos.
 *
 * Uma única chamada `GET /assets/dashboard` retorna todos os 4 blocos
 * (general, financial, operational, risk) para evitar cascata de queries.
 *
 * Filtros globais (costCenterId, locationId) afetam TUDO (S06.5). Sem
 * polling — staleTime 5min + refetchOnWindowFocus dá refresh natural
 * quando o usuário volta pra aba.
 *
 * KPIs de risco são clicáveis via querystring da listagem
 * (`withoutCustodian=true`, `warrantyExpiringInDays=90`) — o backend já
 * expõe esses filtros para completar o click-through.
 */
interface DashboardProps {
  /** Quando true, suprime o PageContainer externo — usado como aba
   *  dentro de AtivosHomePage. */
  embedded?: boolean;
}

export default function DashboardAtivosPage({ embedded = false }: DashboardProps) {
  const [filters, setFilters] = useState<{ costCenterId?: string; locationId?: string }>({});
  const { data, isLoading, isError, error, refetch, isFetching } = useDashboardAtivos(filters);

  const Wrap = ({ children }: { children: ReactNode }) =>
    embedded ? (
      <div className="space-y-6">{children}</div>
    ) : (
      <PageContainer
        title="Dashboard de Ativos"
        subtitle={data ? `Atualizado em ${new Date(data.generatedAt).toLocaleString('pt-BR')}${isFetching ? ' • atualizando…' : ''}` : undefined}
      >
        {children}
      </PageContainer>
    );

  if (isLoading) {
    return (
      <Wrap>
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Carregando dashboard…</span>
        </div>
      </Wrap>
    );
  }

  if (isError || !data) {
    return (
      <Wrap>
        <Alert
          variant="error"
          title="Não foi possível carregar o dashboard."
          action={
            <Button variant="danger" size="sm" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          }
        >
          {(error as Error)?.message ?? 'Erro desconhecido.'}
        </Alert>
      </Wrap>
    );
  }

  const { general, financial, operational, risk } = data;
  const cc = filters.costCenterId ? `&costCenterId=${filters.costCenterId}` : '';
  const loc = filters.locationId ? `&locationId=${filters.locationId}` : '';

  return (
    <Wrap>
      <GlobalFilters
        costCenterId={filters.costCenterId}
        locationId={filters.locationId}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
      />

      {/* Bloco 1 — Geral */}
      <section>
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Panorama</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          <KpiCard label="Total cadastrados" value={general.total} />
          <KpiCard label="Em uso" value={general.inUse} tone="success" to={`/ativos?status=in_use${cc}${loc}`} />
          <KpiCard label="Disponíveis" value={general.available} to={`/ativos?status=available${cc}${loc}`} />
          <KpiCard label="Em manutenção" value={general.inMaintenance} tone="warning" to={`/ativos?status=in_maintenance${cc}${loc}`} />
          <KpiCard label="Em empréstimo" value={general.onLoan} to={`/ativos?status=on_loan${cc}${loc}`} />
          <KpiCard label="Baixados" value={general.retired} to={`/ativos?status=retired${cc}${loc}`} />
          <KpiCard label="Não localizados" value={general.notFound} tone="danger" to={`/ativos?status=not_found${cc}${loc}`} />
        </div>
      </section>

      {/* Bloco 2 — Financeiro */}
      <section>
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Financeiro</h2>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <KpiCard label="Valor total do patrimônio" value={brl(financial.totalValue)} />
          <div className="lg:col-span-2" />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <CategoryValueChart data={financial.byCategory} />
          <CostCenterDonutChart data={financial.byCostCenter} />
        </div>
      </section>

      {/* Bloco 3 — Operacional */}
      <section>
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Operacional</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <RankingList
            title="Top colaboradores (posse ativa)"
            items={operational.byEmployee}
            labelOf={(r) => r.custodian}
            countOf={(r) => r.count}
          />
          <RankingList
            title="Top prestadores (posse ativa)"
            items={operational.byContractor}
            labelOf={(r) => r.custodian}
            countOf={(r) => r.count}
          />
          <RankingList
            title="Top projetos"
            items={operational.byProject}
            labelOf={(r) => r.project}
            countOf={(r) => r.count}
          />
          <RankingList
            title="Top clientes"
            items={operational.byClient}
            labelOf={(r) => r.client}
            countOf={(r) => r.count}
          />
        </div>
      </section>

      {/* Bloco 4 — Risco */}
      <section>
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Risco</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KpiCard
            label="Garantias vencendo (90d)"
            value={risk.warrantiesExpiring}
            tone={risk.warrantiesExpiring > 0 ? 'warning' : 'default'}
            to={`/ativos?warrantyExpiringInDays=90${cc}${loc}`}
          />
          <KpiCard
            label="Licenças vencendo (90d)"
            value={risk.licensesExpiring}
            tone={risk.licensesExpiring > 0 ? 'warning' : 'default'}
            to={licencaUrl()}
          />
          <KpiCard
            label="Sem responsável (em uso)"
            value={risk.assetsWithoutCustodian}
            tone={risk.assetsWithoutCustodian > 0 ? 'danger' : 'default'}
            to={`/ativos?status=in_use&withoutCustodian=true${cc}${loc}`}
          />
          <KpiCard
            label="Não localizados"
            value={risk.assetsNotFound}
            tone={risk.assetsNotFound > 0 ? 'danger' : 'default'}
            to={`/ativos?status=not_found${cc}${loc}`}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Estes números são recalculados a cada carga do dashboard e podem
          divergir da Central de Alertas (onde valem as ações de ack/resolve).
        </p>
      </section>
    </Wrap>
  );
}
